import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

interface Proje {
    projeID: number;
    projeAdi: string;
}

interface StokBeklemeKategori {
    kategoriAdi: string;
    daireSayisi: number;
    toplamSermaye: number;
    beklemeSuresi: string;
}

interface StokBeklemeDaire {
    daireID: number;
    daireNo: string;
    projeAdi: string;
    listeFiyati: number;
    paraBirimi: string;
    beklemeSuresi: number;
    referansTarih: string | null;
    kategori: string;
    referansTipi: string;
}

interface StokBeklemeRapor {
    kategoriler: StokBeklemeKategori[];
    toplamDaire: number;
    toplamSermaye: number;
    kritikStokDaireler: StokBeklemeDaire[];
    tumDaireler: StokBeklemeDaire[];
    analizTipi: string;
}

type SortField = 'beklemeSuresi' | 'listeFiyati' | 'daireNo';
type SortDir = 'asc' | 'desc';

const KATEGORI_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
    '0-30':   { label: '0-30 Gün',   color: '#16a34a', bg: '#dcfce7', border: '#bbf7d0' },
    '31-90':  { label: '31-90 Gün',  color: '#2563eb', bg: '#dbeafe', border: '#bfdbfe' },
    '90-180': { label: '90-180 Gün', color: '#ea580c', bg: '#fed7aa', border: '#fdba74' },
    '180+':   { label: '180+ Gün',   color: '#dc2626', bg: '#fee2e2', border: '#fecaca' },
};

const REFERANS_TIPI_CONFIG: Record<string, { label: string; icon: string }> = {
    'FiyatGecmisi': { label: 'Fiyat Güncelleme',  icon: '💰' },
    'SunumKaydi':   { label: 'Sunum Kaydı',        icon: '👤' },
    'ProjeEkleme':  { label: 'Proje Ekleme',        icon: '📅' },
};

const StokBekleme: React.FC = () => {
    const navigate = useNavigate();
    const [projeler, setProjeler] = useState<Proje[]>([]);
    const [selectedProjeID, setSelectedProjeID] = useState<string>('');
    const [agingType, setAgingType] = useState<'PriceUpdate' | 'CustomerInteraction'>('PriceUpdate');
    const [loading, setLoading] = useState(false);
    const [projelerLoading, setProjelerLoading] = useState(true);
    const [raporData, setRaporData] = useState<StokBeklemeRapor | null>(null);

    // Tablo filtreleri
    const [searchDaireNo, setSearchDaireNo] = useState('');
    const [selectedKategoriler, setSelectedKategoriler] = useState<string[]>(['0-30', '31-90', '90-180', '180+']);
    const [sortField, setSortField] = useState<SortField>('beklemeSuresi');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [showOnlyProjeEkleme, setShowOnlyProjeEkleme] = useState(false);

    useEffect(() => { fetchProjeler(); }, []);

    const fetchProjeler = async () => {
        setProjelerLoading(true);
        try {
            const response = await axiosInstance.get('/api/Projeler');
            setProjeler(response.data);
        } catch {
            toast.error('Projeler yüklenirken hata oluştu');
        } finally {
            setProjelerLoading(false);
        }
    };

    const fetchRapor = async (type = agingType, projeId = selectedProjeID) => {
        setLoading(true);
        try {
            const params: Record<string, string> = { agingType: type };
            if (projeId) params.projeID = projeId;
            const response = await axiosInstance.get('/api/StokBekleme', { params });
            setRaporData(response.data);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Rapor yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleAgingTypeChange = (type: 'PriceUpdate' | 'CustomerInteraction') => {
        setAgingType(type);
        if (raporData) fetchRapor(type, selectedProjeID);
    };

    const handleTemizle = () => {
        setSelectedProjeID('');
        setAgingType('PriceUpdate');
        setRaporData(null);
        setSearchDaireNo('');
        setSelectedKategoriler(['0-30', '31-90', '90-180', '180+']);
        setSortField('beklemeSuresi');
        setSortDir('desc');
        setShowOnlyProjeEkleme(false);
    };

    const toggleKategori = (k: string) => {
        setSelectedKategoriler(prev =>
            prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]
        );
    };

    const toggleSort = (field: SortField) => {
        if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDir('desc'); }
    };

    const filteredDaireler = useMemo(() => {
        if (!raporData) return [];
        return raporData.tumDaireler
            .filter(d => selectedKategoriler.includes(d.kategori))
            .filter(d => !searchDaireNo || d.daireNo.toLowerCase().includes(searchDaireNo.toLowerCase()) || d.projeAdi.toLowerCase().includes(searchDaireNo.toLowerCase()))
            .filter(d => !showOnlyProjeEkleme || d.referansTipi === 'ProjeEkleme')
            .sort((a, b) => {
                let diff = 0;
                if (sortField === 'beklemeSuresi') diff = a.beklemeSuresi - b.beklemeSuresi;
                else if (sortField === 'listeFiyati') diff = a.listeFiyati - b.listeFiyati;
                else if (sortField === 'daireNo') diff = a.daireNo.localeCompare(b.daireNo);
                return sortDir === 'asc' ? diff : -diff;
            });
    }, [raporData, selectedKategoriler, searchDaireNo, showOnlyProjeEkleme, sortField, sortDir]);

    const formatCurrency = (v: number) =>
        new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

    const donutChartData = {
        labels: raporData?.kategoriler.map(k => k.kategoriAdi) || [],
        datasets: [{
            label: 'Daire Sayısı',
            data: raporData?.kategoriler.map(k => k.daireSayisi) || [],
            backgroundColor: ['rgba(34,197,94,0.8)', 'rgba(59,130,246,0.8)', 'rgba(251,146,60,0.8)', 'rgba(239,68,68,0.8)'],
            borderColor: ['rgba(34,197,94,1)', 'rgba(59,130,246,1)', 'rgba(251,146,60,1)', 'rgba(239,68,68,1)'],
            borderWidth: 2,
        }]
    };

    const donutChartOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom' as const, labels: { font: { size: 12 }, padding: 15, usePointStyle: true, pointStyle: 'circle' } },
            tooltip: {
                backgroundColor: 'rgba(17,24,39,0.95)', padding: 12, cornerRadius: 8,
                callbacks: { label: (ctx: any) => `${ctx.label}: ${ctx.parsed} daire` }
            }
        }
    };

    const SortIcon = ({ field }: { field: SortField }) => (
        <span style={{ marginLeft: '4px', opacity: sortField === field ? 1 : 0.3, fontSize: '11px' }}>
            {sortField === field ? (sortDir === 'desc' ? '▼' : '▲') : '⇅'}
        </span>
    );

    return (
        <div style={{ padding: '24px', fontFamily: "'Inter','Roboto',sans-serif", color: '#1f2937' }}>

            {/* Başlık */}
            <div style={{ marginBottom: '28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <button onClick={() => navigate('/proje-raporlari')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px', padding: '4px 8px' }} title="Raporlara Dön">
                        <i className="fas fa-arrow-left" />
                    </button>
                    <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.025em' }}>
                        Stokta Bekleme Raporu
                    </h1>
                </div>
                <p style={{ fontSize: '13.5px', color: '#6b7280', margin: 0, paddingLeft: '48px' }}>
                    Satılmayan dairelerin bekleme süresini ve maliyetini analiz edin
                </p>
            </div>

            {/* Filtre Kartı */}
            <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Filtreler</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px,1fr))', gap: '16px', marginBottom: '20px' }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#4b5563', marginBottom: '6px' }}>Proje</label>
                        <select value={selectedProjeID} onChange={e => setSelectedProjeID(e.target.value)} disabled={projelerLoading}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '13px', color: '#374151', outline: 'none', background: '#fff' }}>
                            <option value="">{projelerLoading ? 'Yükleniyor...' : 'Tüm Projeler'}</option>
                            {projeler.map(p => <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>)}
                        </select>
                    </div>
                </div>

                {/* Analiz Modu */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#4b5563', marginBottom: '8px' }}>Analiz Modu</label>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        {(['PriceUpdate', 'CustomerInteraction'] as const).map(type => (
                            <button key={type} onClick={() => handleAgingTypeChange(type)} disabled={loading}
                                style={{
                                    padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                                    border: agingType === type ? '2px solid #d97706' : '1.5px solid #e2e8f0',
                                    background: agingType === type ? '#fffbeb' : '#fff',
                                    color: agingType === type ? '#d97706' : '#6b7280',
                                    display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s'
                                }}>
                                {type === 'PriceUpdate' ? (
                                    <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>Fiyat Güncelleme Yaşı</>
                                ) : (
                                    <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>Müşteri Etkileşim Yaşı</>
                                )}
                            </button>
                        ))}
                    </div>
                    <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px', fontStyle: 'italic' }}>
                        {agingType === 'PriceUpdate' ? '💡 Son fiyat değişikliği tarihinden bugüne kadar geçen süre' : '💡 Son müşteri sunumu tarihinden bugüne kadar geçen süre'}
                    </p>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                    <button onClick={() => fetchRapor()} disabled={loading}
                        style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: loading ? '#9ca3af' : '#d97706', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {loading ? <><i className="fas fa-spinner fa-spin" />Yükleniyor...</> : <><i className="fas fa-clock" />Rapor Oluştur</>}
                    </button>
                    <button onClick={handleTemizle} disabled={loading}
                        style={{ padding: '10px 24px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#fff', color: '#6b7280', fontSize: '13px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
                        <i className="fas fa-redo" style={{ marginRight: '6px' }} />Temizle
                    </button>
                </div>
            </div>

            {raporData && (
                <>
                    {/* Özet Kartları */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: '16px', marginBottom: '24px' }}>
                        {[
                            { label: 'Toplam Stok Daire', value: raporData.toplamDaire, color: '#111827' },
                            { label: 'Toplam Atıl Sermaye', value: `₺${formatCurrency(raporData.toplamSermaye)}`, color: '#d97706' },
                            { label: 'Kritik Stok (180+)', value: raporData.kritikStokDaireler.length, color: '#ef4444' },
                            { label: 'Referans Tarihi Olmayan', value: raporData.tumDaireler.filter(d => d.referansTipi === 'ProjeEkleme').length, color: '#6b7280' },
                        ].map(card => (
                            <div key={card.label} style={{ background: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 600 }}>{card.label}</div>
                                <div style={{ fontSize: '28px', fontWeight: 700, color: card.color }}>{card.value}</div>
                            </div>
                        ))}
                    </div>

                    {/* Chart + Kategori */}
                    {raporData.kategoriler.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px,1fr))', gap: '24px', marginBottom: '24px' }}>
                            <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{ width: '4px', height: '16px', background: '#d97706', borderRadius: '2px' }} />Stok Yaşlandırma Dağılımı
                                </h3>
                                <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Doughnut data={donutChartData} options={donutChartOptions} />
                                </div>
                            </div>
                            <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{ width: '4px', height: '16px', background: '#059669', borderRadius: '2px' }} />Kategori Detayları
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {raporData.kategoriler.map((kategori, idx) => {
                                        const cfgKey = ['0-30', '31-90', '90-180', '180+'][idx];
                                        const cfg = KATEGORI_CONFIG[cfgKey];
                                        return (
                                            <div key={idx} style={{ background: cfg.bg, borderRadius: '12px', padding: '16px', border: `1.5px solid ${cfg.border}` }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: cfg.color }} />
                                                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>{kategori.kategoriAdi}</span>
                                                    </div>
                                                    <span style={{ fontSize: '18px', fontWeight: 700, color: cfg.color }}>{kategori.daireSayisi} daire</span>
                                                </div>
                                                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                                    Toplam: <span style={{ fontWeight: 600, color: '#111827' }}>₺{formatCurrency(kategori.toplamSermaye)}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tüm Daireler Tablosu */}
                    <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden', marginBottom: '24px' }}>
                        {/* Tablo Başlığı & Filtreler */}
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                                <div>
                                    <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ width: '4px', height: '16px', background: '#6366f1', borderRadius: '2px' }} />
                                        Tüm Daireler
                                        <span style={{ fontSize: '13px', fontWeight: 500, color: '#6b7280', marginLeft: '4px' }}>({filteredDaireler.length} daire)</span>
                                    </h3>
                                    <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>Bekleme süresine, fiyata veya daire no'ya göre sıralayın ve filtreleyin</p>
                                </div>
                                {/* Arama */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', borderRadius: '8px', padding: '8px 12px', border: '1.5px solid #e2e8f0', minWidth: '220px' }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                    <input
                                        placeholder="Daire no veya proje ara..."
                                        value={searchDaireNo}
                                        onChange={e => setSearchDaireNo(e.target.value)}
                                        style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '13px', color: '#374151', width: '100%' }}
                                    />
                                </div>
                            </div>

                            {/* Kategori Filtre Butonları */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>Kategori:</span>
                                {Object.entries(KATEGORI_CONFIG).map(([key, cfg]) => {
                                    const isActive = selectedKategoriler.includes(key);
                                    const count = raporData.tumDaireler.filter(d => d.kategori === key).length;
                                    return (
                                        <button key={key} onClick={() => toggleKategori(key)}
                                            style={{
                                                padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                                                border: `1.5px solid ${isActive ? cfg.color : '#e2e8f0'}`,
                                                background: isActive ? cfg.bg : '#fff',
                                                color: isActive ? cfg.color : '#9ca3af',
                                            }}>
                                            {cfg.label} ({count})
                                        </button>
                                    );
                                })}
                                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <input type="checkbox" id="projeEklemeCheck" checked={showOnlyProjeEkleme} onChange={e => setShowOnlyProjeEkleme(e.target.checked)}
                                        style={{ cursor: 'pointer' }} />
                                    <label htmlFor="projeEklemeCheck" style={{ fontSize: '12px', color: '#6b7280', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                        📅 Sadece referans tarihi olmayanlar
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Tablo */}
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Kategori</th>
                                        <th onClick={() => toggleSort('daireNo')} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                            Daire No <SortIcon field="daireNo" />
                                        </th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Proje</th>
                                        <th onClick={() => toggleSort('listeFiyati')} style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                            Liste Fiyatı <SortIcon field="listeFiyati" />
                                        </th>
                                        <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Referans Tarihi</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Referans Tipi</th>
                                        <th onClick={() => toggleSort('beklemeSuresi')} style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                            Bekleme Süresi <SortIcon field="beklemeSuresi" />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDaireler.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                                                Filtreye uyan daire bulunamadı
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredDaireler.map((daire, idx) => {
                                            const cfg = KATEGORI_CONFIG[daire.kategori] || KATEGORI_CONFIG['180+'];
                                            const refCfg = REFERANS_TIPI_CONFIG[daire.referansTipi] || { label: daire.referansTipi, icon: '📌' };
                                            return (
                                                <tr key={daire.daireID}
                                                    style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa', transition: 'background 0.1s' }}
                                                    onMouseEnter={e => (e.currentTarget.style.background = '#f0f9ff')}
                                                    onMouseLeave={e => (e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafafa')}>
                                                    <td style={{ padding: '12px 16px' }}>
                                                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
                                                            {cfg.label}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 16px', fontWeight: 700, color: '#111827' }}>{daire.daireNo}</td>
                                                    <td style={{ padding: '12px 16px', color: '#4b5563' }}>{daire.projeAdi}</td>
                                                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#111827' }}>
                                                        {daire.paraBirimi === 'TRY' ? '₺' : daire.paraBirimi} {formatCurrency(daire.listeFiyati)}
                                                    </td>
                                                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#6b7280' }}>
                                                        {daire.referansTarih ? new Date(daire.referansTarih).toLocaleDateString('tr-TR') : '—'}
                                                    </td>
                                                    <td style={{ padding: '12px 16px', color: daire.referansTipi === 'ProjeEkleme' ? '#9ca3af' : '#374151', fontSize: '12px' }}>
                                                        {refCfg.icon} {refCfg.label}
                                                    </td>
                                                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                                        <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontWeight: 700, fontSize: '13px', background: cfg.bg, color: cfg.color }}>
                                                            {daire.beklemeSuresi} gün
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* Veri Yok */}
            {!loading && !raporData && (
                <div style={{ background: '#fff', borderRadius: '16px', padding: '60px 24px', border: '1px solid #e2e8f0', textAlign: 'center', color: '#9ca3af' }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px', display: 'block' }}>
                        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                    </svg>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>Raporu görmek için filtreleri seçip "Rapor Oluştur" butonuna tıklayın.</p>
                </div>
            )}
        </div>
    );
};

export default StokBekleme;
